import styled from "@emotion/styled";
import { capitalize, flatten } from "lodash";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";

import {
  Button,
  DesignToken,
  RadiusToken,
  ShadowToken,
  SpacingToken,
  Tag,
  Text,
} from "@livechat/design-system-react-components";

import { ITicket } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents/interfaces";
import { ITag } from "store/entities/tags/interfaces";
import { ITeam } from "store/entities/teams/interfaces";
import { withVar } from "themes/helpers";

import PriorityLabel from "./ticket-details/PriorityLabel";

interface IProps {
  ticket: ITicket;
  selectedChildTickets: ITicket[];
  tags: ITag[];
  teams: ITeam[];
  agents: IAgent[];
}

interface IOverflowTooltipProps {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collection: Record<string, any>[];
  limit?: number;
  toggleShow(arg: boolean): void;
  isExpanded: boolean;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const OverflowTooltip = ({ collection, limit = 1, toggleShow, isExpanded = false }: IOverflowTooltipProps) => {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const toggleExpand = () => {
    if (isExpanded) {
      toggleShow(false);
    } else toggleShow(true);
  };

  enum expandLabel {
    showMore = "Show more",
    showLess = "Show less",
  }

  return (
    collection.length > limit && (
      <>
        {!isExpanded && <Text>...</Text>}
        <Button kind="link-light" data-testid="merge-tickets-modal-show-more-button" onClick={toggleExpand}>
          {isExpanded ? expandLabel.showLess : expandLabel.showMore}
        </Button>
      </>
    )
  );
};

export const MergedTicketSummary: React.FC<IProps> = ({ ticket, selectedChildTickets, tags, teams, agents }) => {
  const [tagsExpanded, expandTags] = useState<boolean>(false);
  const [followersExpanded, expandFollowers] = useState<boolean>(false);
  const [ccsExpanded, expandCcs] = useState<boolean>(false);

  const getMergedArray = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: any) => {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const secondArray = ticket[key] ? ticket[key] : [];
      return [
        ...new Set([
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          ...flatten(selectedChildTickets.map(item => (item[key] !== undefined ? item[key] : null))),
          ...secondArray,
        ]),
      ];
    },
    [selectedChildTickets, ticket]
  );

  const mergedTags = useMemo(
    () => tags.filter(tag => getMergedArray("tagIds").includes(tag.id)),
    [getMergedArray, tags]
  );
  const mergedTeams = useMemo(() => teams.filter(team => ticket.teamIds.includes(team.id)), [teams, ticket.teamIds]);
  const mergedAgent = useMemo(
    () => agents.find(agent => agent.id === ticket.assignment.agent?.id),
    [agents, ticket.assignment.agent]
  );
  const mergedFollowers = useMemo(
    () => agents.filter(agent => getMergedArray("followers").includes(agent.id)),
    [agents, getMergedArray]
  );

  let mergedCcs = useMemo(() => getMergedArray("ccs").filter(Boolean), [getMergedArray]);
  const childParentRequesterDiff = selectedChildTickets.filter(item => item.requester.email !== ticket.requester.email);
  if (childParentRequesterDiff.length) {
    mergedCcs = [...mergedCcs, ...childParentRequesterDiff.map(item => item.requester)];
  }

  return (
    <Wrapper>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Status:</Text>
        <Text data-testid="merged-ticket-summary-status">{capitalize(ticket.status)}</Text>
      </Group>
      <Group data-testid="merged-ticket-summary-priority">
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Priority:</Text>
        <PriorityLabel priority={ticket.priority} />
      </Group>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Tags:</Text>
        <>
          {mergedTags.length > 0 &&
            (!tagsExpanded
              ? mergedTags.map(
                  (tag, i) =>
                    i < 3 && (
                      <Tag
                        size="small"
                        kind="warning"
                        key={tag?.id}
                        data-testid={`merged-ticket-summary-tag-${tag?.name}`}
                      >
                        {tag?.name}
                      </Tag>
                    )
                )
              : mergedTags.map(tag => (
                  <Tag kind="warning" size="small" key={tag?.id} data-testid={`merged-ticket-summary-tag-${tag?.name}`}>
                    {tag?.name}
                  </Tag>
                )))}{" "}
          {mergedTags.length === 0 && (
            <Text customColor={withVar(DesignToken.ContentBasicDisabled)}>
              There are no tags associated with this ticket
            </Text>
          )}
        </>
        {mergedTags.length > 3 && (
          <OverflowTooltip toggleShow={expandTags} isExpanded={tagsExpanded} collection={mergedTags} limit={3} />
        )}
      </Group>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Team:</Text>
        {mergedTeams.map(team => (
          <Text key={team?.id} data-testid="merged-ticket-summary-team">
            {team?.name}
          </Text>
        ))}
      </Group>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Agents:</Text>
        {ticket.assignment.agent?.id ? (
          <Text data-testid="merged-ticket-summary-agents">{mergedAgent?.name}</Text>
        ) : (
          <Text customColor={withVar(DesignToken.ContentBasicDisabled)}>
            There are no agents assigned to this ticket.
          </Text>
        )}
      </Group>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Followers:</Text>
        <>
          {mergedFollowers.length > 0 &&
            (!followersExpanded ? (
              <Text>{mergedFollowers[0]?.name}</Text>
            ) : (
              mergedFollowers.map((item, i) => (
                <Text key={item?.id} data-testid={`merged-ticket-summary-followers-${item?.id}`}>
                  {item?.name}
                  {i + 1 !== mergedFollowers.length && ","}
                </Text>
              ))
            ))}{" "}
          {mergedFollowers.length === 0 && (
            <Text customColor={withVar(DesignToken.ContentBasicDisabled)}>
              There are no followers assigned to this ticket.
            </Text>
          )}
        </>
        {mergedFollowers.length > 1 && (
          <OverflowTooltip isExpanded={followersExpanded} toggleShow={expandFollowers} collection={mergedFollowers} />
        )}
      </Group>
      <Group>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>People in the loop:</Text>
        <>
          {mergedCcs.length > 0 && !ccsExpanded ? (
            <Text data-testid={`merged-ticket-summary-people-in-the-loop-${mergedCcs[0].email}`}>
              {mergedCcs[0].email}
            </Text>
          ) : (
            mergedCcs.map((item, i) => (
              <Text key={item.email} data-testid={`merged-ticket-summary-people-in-the-loop-${item.email}`}>
                {item.email}
                {i + 1 !== mergedCcs.length && ","}
              </Text>
            ))
          )}
          {mergedCcs.length === 0 && (
            <Text customColor={withVar(DesignToken.ContentBasicDisabled)}>
              There are no people in the loop for this ticket.
            </Text>
          )}
        </>
        {mergedCcs.length > 1 && (
          <OverflowTooltip isExpanded={ccsExpanded} collection={mergedCcs} toggleShow={expandCcs} />
        )}
      </Group>
    </Wrapper>
  );
};

const Group = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  overflow: hidden;
  flex-wrap: wrap;
  gap: var(${SpacingToken.Spacing1});
  padding: var(${SpacingToken.Spacing2}) 0;
  &:not(:last-child) {
    box-shadow: var(${ShadowToken.DividerBottom});
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing4});
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  box-sizing: border-box;
  border-radius: var(${RadiusToken.Radius3});
  justify-content: space-between;
  margin-bottom: var(${SpacingToken.Spacing1});
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
`;
